import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, X } from 'lucide-react';
import api from '@/lib/api';

/* SandboxRibbon
 * ─────────────
 * Soft amber strip across the top of every dashboard page when the current
 * tenant has any sandbox-tagged records. Disappears immediately after the
 * nuke endpoint runs. The user can also dismiss it for the session.
 */
export default function SandboxRibbon() {
  const [total, setTotal] = useState(0);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem('ve_sandbox_ribbon_dismissed') === '1'; } catch { return false; }
  });

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        // ── GO LIVE gate ── If the platform is live the sandbox is locked
        // and `/sandbox/status` returns 410. Either way, the ribbon is
        // meaningless in production — bail before even asking.
        const s = await api.get('/system/state').catch(() => null);
        if (s?.data?.is_live) {
          if (!cancelled) setTotal(0);
          return;
        }
        const { data } = await api.get('/sandbox/status');
        if (!cancelled) setTotal(data?.total || 0);
      } catch { /* hide silently when 403 (non-admin) */ }
    };
    tick();
    // Re-check every 30s so the ribbon disappears after a nuke without a refresh.
    const t = setInterval(tick, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (total === 0 || dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem('ve_sandbox_ribbon_dismissed', '1'); } catch { /* no-op */ }
  };

  return (
    <div
      className="sticky top-0 z-40 w-full bg-amber-50 dark:bg-amber-900/25 border-b border-amber-300 dark:border-amber-400/40 px-4 py-1.5 flex items-center gap-3 text-[11px]"
      data-testid="sandbox-ribbon"
    >
      <AlertTriangle size={13} className="text-amber-700 dark:text-amber-300 shrink-0"/>
      <div className="flex-1 truncate text-amber-900 dark:text-amber-100">
        <span className="font-bold uppercase tracking-[0.18em] mr-2">Demo data active</span>
        <span className="opacity-90">{total} <code className="mono text-[10px] bg-amber-100 dark:bg-amber-900/40 px-1 rounded">sandbox:true</code> records present — nuke before launch.</span>
      </div>
      <Link
        to="/dashboard/sandbox"
        className="text-amber-900 dark:text-amber-100 font-bold uppercase tracking-[0.18em] hover:underline shrink-0"
        data-testid="sandbox-ribbon-link"
      >
        Manage →
      </Link>
      <button
        onClick={dismiss}
        className="text-amber-700 dark:text-amber-300 hover:text-rose-500 shrink-0"
        data-testid="sandbox-ribbon-dismiss"
        title="Dismiss for this session"
      >
        <X size={13}/>
      </button>
    </div>
  );
}
